import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp } from 'lucide-react';
import { Chart, registerables } from 'chart.js';
import { TpaAxiosInstance }              from '../../Network/Tpa';
import { PayerAxiosInstance }            from '../../Network/Payer';
import { NetworkAxiosInstance }          from '../../Network/network';
import { PolicyAxiosInstance }           from '../../Network/policy';
import { DoctorAxiosInstance }           from '../../Network/Doctor';
import { SpecialtyAxiosInstance }        from '../../Network/Specialty';
import { ActivityRuleAxiosInstance }     from '../../Network/ActivityRule';
import { IcdRuleAxiosInstance }          from '../../Network/IcdRule';
import { CustomActivityAxiosInstance }   from '../../Network/CustomActivity';
import { CustomIcdAxiosInstance }        from '../../Network/CustomIcd';
import { MedicalActivityAxiosInstance }  from '../../Network/medicalActivity';
import { MedicalIcdAxiosInstance }       from '../../Network/medicalIcd';
import styles from './Dashboard.module.css';
import {
  IconUsers,
  IconStethoscope,
  IconHeartbeat,
  IconNetwork,
  IconShieldCheck,
  IconCrown,
  IconCalendar,
  IconShieldStar,
  IconVirus,
  IconStack2,
  IconTags,
  IconDna,
} from '@tabler/icons-react';

Chart.register(...registerables);

/* ─── Entity config ─── */
const ENTITY_CONFIG = [
  { key: 'TPA',                  label: 'TPA',                   icon: IconUsers,          color: 'blue'   },
  { key: 'Payers',               label: 'Payers',                icon: IconStethoscope,    color: 'teal'   },
  { key: 'Doctors',              label: 'Doctors',               icon: IconHeartbeat,      color: 'amber'  },
  { key: 'Networks',             label: 'Networks',              icon: IconNetwork,        color: 'coral'  },
  { key: 'Policies',             label: 'Policies',              icon: IconShieldCheck,    color: 'purple' },
  { key: 'Specialty',            label: 'Specialty',             icon: IconCrown,          color: 'pink'   },
  { key: 'ActivityRules',        label: 'Activity Rules',        icon: IconShieldStar,     color: 'green'  },
  { key: 'IcdRules',             label: 'ICD Rules',             icon: IconVirus,          color: 'indigo' },
  { key: 'CustomActivityGroup',  label: 'Custom Activity Groups',icon: IconStack2,         color: 'rose'   },
  { key: 'CustomIcdGroup',       label: 'Custom ICD Groups',     icon: IconTags,           color: 'orange' },
  { key: 'MedicalActivityGroup', label: 'Medical Activity Groups',icon: IconDna,          color: 'cyan'   },
  { key: 'MedicalIcdGroup',      label: 'Medical ICD Groups',    icon: IconDna,            color: 'sky'    },
];

const COLOR_MAP: Record<string, { main: string; light: string; dark: string }> = {
  blue:   { main: '#378ADD', light: '#E6F1FB', dark: '#0C447C' },
  teal:   { main: '#1D9E75', light: '#E1F5EE', dark: '#085041' },
  amber:  { main: '#BA7517', light: '#FAEEDA', dark: '#633806' },
  coral:  { main: '#D85A30', light: '#FAECE7', dark: '#4A1B0C' },
  purple: { main: '#7F77DD', light: '#EEEDFE', dark: '#3C3489' },
  pink:   { main: '#D4537E', light: '#FBEAF0', dark: '#4B1528' },
  green:  { main: '#639922', light: '#EAF3DE', dark: '#173404' },
  indigo: { main: '#4F5BD5', light: '#ECEFFE', dark: '#1A2070' },
  rose:   { main: '#C0404A', light: '#FCEBEB', dark: '#501313' },
  orange: { main: '#D97706', light: '#FEF3C7', dark: '#78350F' },
  cyan:   { main: '#0891B2', light: '#E0F7FA', dark: '#164E63' },
  sky:    { main: '#0284C7', light: '#E0F2FE', dark: '#0C4A6E' },
};

interface EntityStats {
  total: number;
  active: number;
  inactive: number;
}

type StatsMap = Record<string, EntityStats>;

/* ─── helpers ─── */
const parse = (res: any): any[] =>
  res.data?.data?.items ??
  res.data?.data ??
  res.data?.result ??
  res.data?.items ??
  [];

const isActive = (item: any) =>
  item.status === true || item.status === 'Active' || item.isActive === true;

const toStats = (arr: any[]): EntityStats => ({
  total:    arr.length,
  active:   arr.filter(isActive).length,
  inactive: arr.filter((i) => !isActive(i)).length,
});

/* ─── Component ─── */
const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsMap>({});
  const [currentDate, setCurrentDate] = useState(new Date());

  const barRef     = useRef<HTMLCanvasElement>(null);
  const donutRef   = useRef<HTMLCanvasElement>(null);
  const barChart   = useRef<Chart | null>(null);
  const donutChart = useRef<Chart | null>(null);

  /* live clock */
  useEffect(() => {
    const id = setInterval(() => setCurrentDate(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  /* fetch */
  useEffect(() => {
    fetchAllData();
  }, []);

  /* charts */
  useEffect(() => {
    if (!Object.keys(stats).length) return;
    drawCharts();
    return () => {
      barChart.current?.destroy();
      donutChart.current?.destroy();
    };
   
  }, [stats]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const tempStats: StatsMap = {};
      const fetchTask = async (key: string, axiosInstance: any, endpoint: string) => {
        try {
          const res = await axiosInstance.get(endpoint);
          const data = (key === 'Policies' && Array.isArray(res.data?.data))
            ? res.data.data
            : parse(res);

          tempStats[key] = toStats(data);
        } catch (err) {
          console.error(`Error fetching ${key}:`, err);
          tempStats[key] = { total: 0, active: 0, inactive: 0 };
        }
      };

      await Promise.allSettled([
        fetchTask('TPA', TpaAxiosInstance, 'tpas'),
        fetchTask('Payers', PayerAxiosInstance, 'payers'),
        fetchTask('Networks', NetworkAxiosInstance, 'networks'),
        fetchTask('Policies', PolicyAxiosInstance, 'policies'),
        fetchTask('Doctors', DoctorAxiosInstance, 'doctors'),
        fetchTask('Specialty', SpecialtyAxiosInstance, 'specialties'),
        fetchTask('ActivityRules', ActivityRuleAxiosInstance, 'activity-validation-rules'),
        fetchTask('IcdRules', IcdRuleAxiosInstance, 'icd-validation-rules'),
        fetchTask('CustomActivityGroup', CustomActivityAxiosInstance, 'custom-activity-group'),
        fetchTask('CustomIcdGroup', CustomIcdAxiosInstance, 'custom-icd-group'),
        fetchTask('MedicalActivityGroup', MedicalActivityAxiosInstance, 'medical-group/activity'),
        fetchTask('MedicalIcdGroup', MedicalIcdAxiosInstance, 'medical-group/icd'),
      ]);

      setStats(tempStats);
    } catch (err) {
      console.error('Dashboard general fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  /* ── charts ── */
  const drawCharts = () => {
    barChart.current?.destroy();
    donutChart.current?.destroy();

    /* Only show first 6 config entities in charts to keep them readable */
    const chartEntities = ENTITY_CONFIG.slice(0, 6);
    const labels  = chartEntities.map((e) => e.label);
    const mains   = chartEntities.map((e) => COLOR_MAP[e.color].main);
    const lights  = chartEntities.map((e) => COLOR_MAP[e.color].light);

    if (barRef.current) {
      barChart.current = new Chart(barRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Active',
              data: chartEntities.map((e) => stats[e.key]?.active ?? 0),
              backgroundColor: mains,
              borderRadius: 5,
              borderSkipped: false,
            },
            {
              label: 'Inactive',
              data: chartEntities.map((e) => stats[e.key]?.inactive ?? 0),
              backgroundColor: lights,
              borderColor: mains,
              borderWidth: 1,
              borderRadius: 5,
              borderSkipped: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 11 } } },
            y: { grid: { color: 'rgba(150,150,150,0.1)' }, ticks: { font: { size: 11 } } },
          },
        },
      });
    }

    if (donutRef.current) {
      donutChart.current = new Chart(donutRef.current, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: chartEntities.map((e) => stats[e.key]?.total ?? 0),
            backgroundColor: mains,
            borderWidth: 2,
            borderColor: 'transparent',
            hoverOffset: 6,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: { legend: { display: false } },
        },
      });
    }
  };

  /* ── helpers ── */
  const pct = (active: number, total: number) =>
    total > 0 ? Math.round((active / total) * 100) : 0;

  const totalEntities = Object.values(stats).reduce((s, v) => s + v.total,  0);
  const totalActive   = Object.values(stats).reduce((s, v) => s + v.active, 0);
  const totalInactive = totalEntities - totalActive;

  const healthClass = (p: number) =>
    p >= 80 ? styles.badgeHealthy : p >= 50 ? styles.badgeFair : styles.badgeLow;
  const healthLabel = (p: number) =>
    p >= 80 ? 'Healthy' : p >= 50 ? 'Fair' : 'Low';

  const sortedByRate = [...ENTITY_CONFIG].sort(
    (a, b) =>
      pct(stats[b.key]?.active ?? 0, stats[b.key]?.total ?? 1) -
      pct(stats[a.key]?.active ?? 0, stats[a.key]?.total ?? 1),
  );

  /* ── loading ── */
  if (loading) {
    return (
      <div className={styles.dashboardContainer} data-testid="Dashboard-page">
        <div className={styles.loadingContainer} data-testid="Dashboard-loading">
          <div className={styles.loader} />
          <p>Loading Dashboard…</p>
        </div>
      </div>
    );
  }

  /* ── render ── */
  return (
    <div className={styles.dashboardContainer} data-testid="Dashboard-page">

      {/* Header */}
      <header className={styles.dashboardHeader} data-testid="Dashboard-header">
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <TrendingUp size={22} />
          </div>
          <div>
            <div className={styles.headerTitle}>QUDRA Healthcare</div>
            <div className={styles.headerSub}>Advanced System Analytics</div>
          </div>
        </div>
        <div className={styles.dateDisplay} data-testid="Dashboard-date-display">
          <IconCalendar size={18} stroke={2} color="white" />
          <span>
            {currentDate.toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
          </span>
        </div>
      </header>

      {/* Summary */}
      <div className={styles.summaryRow} data-testid="Dashboard-summary-row">
        <div className={`${styles.summaryCard} ${styles.total}`} data-testid="Dashboard-summary-total">
          <div className={styles.summaryLabel}>Total Entities</div>
          <div className={styles.summaryValue}>{totalEntities}</div>
          <div className={styles.summarySub}>across all modules</div>
        </div>
        <div className={`${styles.summaryCard} ${styles.active}`} data-testid="Dashboard-summary-active">
          <div className={styles.summaryLabel}>Active</div>
          <div className={styles.summaryValue}>{totalActive}</div>
          <div className={styles.summarySub}>{pct(totalActive, totalEntities)}% activation rate</div>
        </div>
        <div className={`${styles.summaryCard} ${styles.inactive}`} data-testid="Dashboard-summary-inactive">
          <div className={styles.summaryLabel}>Inactive</div>
          <div className={styles.summaryValue}>{totalInactive}</div>
          <div className={styles.summarySub}>{pct(totalInactive, totalEntities)}% offline</div>
        </div>
      </div>

      {/* ── Section 1: Configuration Entities ── */}
      <div className={styles.sectionLabel}>Configuration entities</div>
      <div className={styles.entityGrid} data-testid="Dashboard-entityGrid-config">
        {ENTITY_CONFIG.slice(0, 6).map((entity) => {
          const s         = stats[entity.key] ?? { total: 0, active: 0, inactive: 0 };
          const p         = pct(s.active, s.total);
          const colors    = COLOR_MAP[entity.color];
          const Icon      = entity.icon;

          return (
            <div
              key={entity.key}
              className={`${styles.entityCard} ${styles[entity.color]}`}
              data-testid={`Dashboard-entityCard-${entity.key}`}
            >
              <div className={styles.cardTop}>
                <div className={styles.cardIconWrap} style={{ background: colors.light, color: colors.main }}>
                  <Icon size={20} stroke={2} />
                </div>
                <div>
                  <div className={styles.cardCount}>{s.total}</div>
                  <div className={styles.cardName}>{entity.label}</div>
                </div>
              </div>
              <div className={styles.progressBarWrap}>
                <div className={styles.progressBarFill} style={{ width: `${p}%`, background: colors.main }} />
              </div>
              <div className={styles.cardBadges}>
                <span className={styles.badgeActive} style={{ background: colors.light, color: colors.dark }}>
                  Active {s.active}
                </span>
                <span className={styles.badgeInactive}>Off {s.inactive}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Section 2: Rules & Groups ── */}
      <div className={styles.sectionLabel}>Validation rules &amp; code groups</div>
      <div className={styles.entityGrid} data-testid="Dashboard-entityGrid-rules">
        {ENTITY_CONFIG.slice(6).map((entity) => {
          const s         = stats[entity.key] ?? { total: 0, active: 0, inactive: 0 };
          const p         = pct(s.active, s.total);
          const colors    = COLOR_MAP[entity.color];
          const Icon      = entity.icon;

          return (
            <div
              key={entity.key}
              className={`${styles.entityCard} ${styles[entity.color]}`}
              data-testid={`Dashboard-entityCard-${entity.key}`}
            >
              <div className={styles.cardTop}>
                <div className={styles.cardIconWrap} style={{ background: colors.light, color: colors.main }}>
                  <Icon size={20} stroke={2} />
                </div>
                <div>
                  <div className={styles.cardCount}>{s.total}</div>
                  <div className={styles.cardName}>{entity.label}</div>
                </div>
              </div>
              <div className={styles.progressBarWrap}>
                <div className={styles.progressBarFill} style={{ width: `${p}%`, background: colors.main }} />
              </div>
              <div className={styles.cardBadges}>
                <span className={styles.badgeActive} style={{ background: colors.light, color: colors.dark }}>
                  Active {s.active}
                </span>
                <span className={styles.badgeInactive}>Off {s.inactive}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Charts (config entities only) ── */}
      <div className={styles.sectionLabel}>Visual analysis — configuration entities</div>
      <div className={styles.chartsRow} data-testid="Dashboard-chartsRow">
        <div className={styles.chartCard} data-testid="Dashboard-barChart-card">
          <div className={styles.chartTitle}>Active vs Inactive — configuration entities</div>
          <div className={styles.barCanvasWrap}>
            <canvas
              ref={barRef}
              role="img"
              aria-label="Bar chart comparing active and inactive counts"
              data-testid="Dashboard-barChart-canvas"
            />
          </div>
        </div>
        <div className={styles.chartCard} data-testid="Dashboard-donutChart-card">
          <div className={styles.chartTitle}>Distribution by type</div>
          <div className={styles.donutWrap}>
            <div className={styles.donutCanvasWrap}>
              <canvas
                ref={donutRef}
                role="img"
                aria-label="Donut chart showing entity distribution"
                data-testid="Dashboard-donutChart-canvas"
              />
            </div>
            <div className={styles.donutLegend} data-testid="Dashboard-donutChart-legend">
              {ENTITY_CONFIG.slice(0, 6).map((entity) => {
                const s = stats[entity.key] ?? { total: 0 };
                const p = pct(s.total, Object.values(stats).slice(0, 6).reduce((acc, v) => acc + v.total, 0));
                return (
                  <div
                    key={entity.key}
                    className={styles.legendItem}
                    data-testid={`Dashboard-legendItem-${entity.key}`}
                  >
                    <div className={styles.legendDot} style={{ background: COLOR_MAP[entity.color].main }} />
                    <span>{entity.label}</span>
                    <span className={styles.legendVal}>{p}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom Row ── */}
      <div className={styles.bottomRow} data-testid="Dashboard-bottomRow">

        {/* Module status — all 12 */}
        <div className={styles.activityCard} data-testid="Dashboard-moduleStatus-card">
          <div className={styles.chartTitle}>Module status overview</div>
          {ENTITY_CONFIG.map((entity) => {
            const s = stats[entity.key] ?? { total: 0, active: 0, inactive: 0 };
            const p = pct(s.active, s.total);
            const colors = COLOR_MAP[entity.color];
            const Icon = entity.icon;

            return (
              <div
                key={entity.key}
                className={styles.activityItem}
                data-testid={`Dashboard-moduleStatusItem-${entity.key}`}
              >
                <div className={styles.actIconWrap} style={{ background: colors.light }}>
                  <Icon size={16} color={colors.main} stroke={2} />
                </div>
                <div className={styles.actText}>
                  <div className={styles.actTitle}>{entity.label}</div>
                  <div className={styles.actSub}>{s.active} / {s.total} active</div>
                </div>
                <span className={`${styles.actBadge} ${healthClass(p)}`}>
                  {healthLabel(p)} {p}%
                </span>
              </div>
            );
          })}
        </div>

        {/* Activation rate — all 12 */}
        <div className={styles.ratioCard} data-testid="Dashboard-activationRate-card">
          <div className={styles.chartTitle}>Activation rate</div>
          {sortedByRate.map((entity) => {
            const s = stats[entity.key] ?? { total: 0, active: 0 };
            const p = pct(s.active, s.total);
            const colors = COLOR_MAP[entity.color];
            return (
              <div
                key={entity.key}
                className={styles.ratioItem}
                data-testid={`Dashboard-activationRateItem-${entity.key}`}
              >
                <div className={styles.ratioTop}>
                  <span>{entity.label}</span>
                  <span className={styles.ratioTopVal}>{p}%</span>
                </div>
                <div className={styles.ratioBarBg}>
                  <div className={styles.ratioBarFill} style={{ width: `${p}%`, background: colors.main }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;